import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SyncIcon from "@mui/icons-material/Sync";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../../components/HeaderBread";
import Layout from "../../../../Layout";
import Logs from "../../../../components/Logs";
import SwitchBar from "../../../../components/Pages/SwitchBar";
import SearchField from "../../../../components/SearchField";
import SortableTable from "../../../../components/UI/SortableTable";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import { useDebounce } from "../../../../hooks";
import { checkPermission } from "../../../../utils/Validation/checkPermission";
import { addlog } from "../../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../../utils/Constant/LogConstant";
import { getVessels } from "../../../../utils/ApiCalls/SpecialPage/index";
import { getSailingPageData } from "../../../../utils/ApiCalls/SailingData";
import {
  getShorexPackages,
  updateShorexPackage,
  syncShorexExcursions,
  getShorexGuideLangStatus,
  updateShorexGuideLangStatus,
} from "../../../../utils/ApiCalls/BackOffice/Shorex";

const PAGE_PATH = "/shore/back-office/shore-excursions/management";

const AllowedSeatInput = ({ row, onCommit }) => {
  const [value, setValue] = useState(row.allowedSeat);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setValue(row.allowedSeat);
    setInvalid(false);
  }, [row.allowedSeat]);

  const handleChange = (raw) => {
    setValue(raw);
    const num = Number(raw);
    const isInvalid = raw === "" || !Number.isFinite(num) || num < 0 || num > row.maxSeat;
    setInvalid(isInvalid);
    if (!isInvalid) onCommit(row, num);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
      }}
    >
      <IconButton
        size="small"
        disabled={row.allowedSeat <= 0}
        onClick={() => onCommit(row, row.allowedSeat - 1)}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <input
        type="number"
        min={0}
        max={row.maxSeat}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        style={{
          width: "56px",
          textAlign: "center",
          border: invalid ? "1px solid #d32f2f" : "1px solid #ccc",
          borderRadius: "4px",
          padding: "4px",
          backgroundColor: invalid ? "#fdecea" : undefined,
        }}
      />
      <IconButton
        size="small"
        disabled={row.allowedSeat >= row.maxSeat}
        onClick={() => onCommit(row, row.allowedSeat + 1)}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

const CutOffTimeInput = ({ row, onCommit }) => {
  const [invalid, setInvalid] = useState(false);
  const [localValue, setLocalValue] = useState(row.cutOffTime);

  useEffect(() => {
    setLocalValue(row.cutOffTime);
  }, [row.cutOffTime]);

  return (
    <DatePicker
      showTime={{ use12Hours: true, format: "hh:mm A" }}
      format="YYYY-MM-DD hh:mm A"
      value={localValue ? dayjs(localValue) : null}
      onChange={(value) => {
        if (!value) {
          // can't clear it out - must always have a cut off; snap the display
          // back to the real value instead of leaving it looking empty
          setInvalid(true);
          setLocalValue(row.cutOffTime);
          return;
        }
        setInvalid(false);
        onCommit(row, value.toDate().toISOString());
      }}
      disabledDate={(current) => row.startDate && current.isAfter(dayjs(row.startDate), "day")}
      allowClear={false}
      placeholder="No cut off"
      status={invalid ? "error" : undefined}
      className="shorex-date-picker"
      style={{ width: "200px" }}
    />
  );
};

const ShoreExcursionsManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();
  const [syncing, setSyncing] = useState(false);

  const [topTab, setTopTab] = useState("management");

  const [vesselOptions, setVesselOptions] = useState([]);
  const [vesselId, setVesselId] = useState(() => {
    const urlVesselId = new URLSearchParams(location.search).get("vesselId");
    return urlVesselId ? Number(urlVesselId) : "";
  });

  const [sailingOptions, setSailingOptions] = useState([]);
  const [selectedSailing, setSelectedSailing] = useState(null);
  const [sailingLoading, setSailingLoading] = useState(false);

  const [date, setDate] = useState(
    () => new URLSearchParams(location.search).get("date") || ""
  );
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 1000);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortState, setSortState] = useState([]);

  const [guideLangEnabled, setGuideLangEnabled] = useState(false);

  // Load vessels the current user has permission to view on this page.
  useEffect(() => {
    const loadVessels = async () => {
      const vessels = await getVessels();
      const allowed = (vessels || []).filter((item) =>
        checkPermission(
          permission,
          "read_per",
          "Management",
          `${PAGE_PATH}/${item.name.toLowerCase().replaceAll(" ", "-")}`
        )
      );
      setVesselOptions(allowed);

      const currentVesselId = new URLSearchParams(location.search).get(
        "vesselId"
      );
      const isCurrentAllowed = allowed.some(
        (v) => String(v.id) === String(currentVesselId)
      );
      if (!isCurrentAllowed && allowed.length) {
        setVesselId(allowed[0].id);
      }
    };
    loadVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  // Fetch this vessel's guide-language toggle status.
  useEffect(() => {
    if (!vesselId) return;
    const loadGuideLangStatus = async () => {
      const res = await getShorexGuideLangStatus(vesselId);
      setGuideLangEnabled(!!res?.data?.guideLangEnabled);
    };
    loadGuideLangStatus();
  }, [vesselId]);

  const handleGuideLangToggle = async () => {
    const nextValue = !guideLangEnabled;
    const vesselName = vesselOptions.find((v) => v.id === vesselId)?.name;
    setGuideLangEnabled(nextValue);
    const res = await updateShorexGuideLangStatus(vesselId, nextValue);
    if (res?.error) {
      setGuideLangEnabled(!nextValue);
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: res.error, type: "error" },
      });
    } else {
      addlog(
        `Guide Language ${nextValue ? LOG_ACTIONS.ACTIVATED : LOG_ACTIONS.DEACTIVATED}`,
        LOG_TYPES.SHOREX,
        { title: vesselName },
        userDetails.id
      );
    }
  };

  // Fetch every sailing for the vessel, so the admin can browse any of them,
  // and pick a sensible default: the active sailing, else the nearest upcoming one.
  useEffect(() => {
    if (!vesselId) return;
    const loadSailings = async () => {
      setSailingLoading(true);
      const res = await getSailingPageData({
        vessel_id: vesselId,
        sort: "sailing_date",
        limit: 100,
      });
      const list = res?.data || [];
      setSailingOptions(list);

      const active = list.find((s) => s.status === "active");
      const nextUpcoming = list
        .filter((s) => s.status === "upcoming")
        .sort((a, b) => moment(a.sailing_date).diff(moment(b.sailing_date)))[0];
      setSelectedSailing(active || nextUpcoming || null);
      setSailingLoading(false);
    };
    loadSailings();
  }, [vesselId]);

  // Clear the date (don't auto-fill it) whenever it falls outside the
  // newly-selected sailing's range, or once no sailing is selected at all.
  useEffect(() => {
    if (!selectedSailing?.sailing_date) {
      setDate("");
      return;
    }
    const start = moment(selectedSailing.sailing_date).format("YYYY-MM-DD");
    const end = moment(selectedSailing.sailing_date)
      .add(selectedSailing.duration, "days")
      .format("YYYY-MM-DD");
    const withinRange = date && date >= start && date <= end;
    if (!withinRange) {
      setDate("");
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSailing]);

  // Keep the URL in sync with vessel/date so the page is shareable/refreshable.
  useEffect(() => {
    const params = new URLSearchParams();
    if (vesselId) params.set("vesselId", vesselId);
    if (date) params.set("date", date);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (params.toString() !== location.search.replace(/^\?/, "")) {
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId, date, page, pageSize, search]);

  // Fetch the excursion list: a specific date if one is picked, otherwise
  // every excursion synced under the selected sailing.
  const loadShorexPackages = async (targetPage) => {
    if (!selectedSailing) return;
    const rangeQuery = date ? { date } : { packageId: selectedSailing.package_id };
    const sort = sortState.length
      ? sortState.map((s) => `${s.key}:${s.direction}`).join(",")
      : undefined;
    const res = await getShorexPackages({
      page: targetPage,
      limit: pageSize,
      ...rangeQuery,
      search: debouncedSearch || undefined,
      sort,
    });
    if (res?.error) return;
    setRows(res.data || []);
    setTotalCount(res.totalCount || 0);
  };

  const handleSortChange = (nextSortState) => {
    setSortState(nextSortState);
    setPage(1);
  };

  useEffect(() => {
    if (topTab !== "management" || !vesselId || !selectedSailing) return;
    loadShorexPackages(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topTab, vesselId, selectedSailing, date, page, pageSize, debouncedSearch, sortState]);

  const handleToggleLink = async (row) => {
    const nextValue = !row.linkEnabled;
    setRows((prev) =>
      prev.map((r) =>
        r.excId === row.excId ? { ...r, linkEnabled: nextValue } : r
      )
    );
    const res = await updateShorexPackage(row.excId, {
      linkEnabled: nextValue,
    });
    if (res?.error) {
      setRows((prev) =>
        prev.map((r) =>
          r.excId === row.excId ? { ...r, linkEnabled: row.linkEnabled } : r
        )
      );
    } else {
      addlog(
        `${row.name} is ` +
          (nextValue ? LOG_ACTIONS.ACTIVATED : LOG_ACTIONS.DEACTIVATED),
        LOG_TYPES.SHOREX,
        { id: row.excId, title: row.name, excNo: row.excNo, packageId: selectedSailing?.package_id },
        userDetails.id
      );
    }
  };

  const handleAllowedSeatChange = async (row, nextValue) => {
    if (nextValue === row.allowedSeat) return;
    const previousValue = row.allowedSeat;
    setRows((prev) =>
      prev.map((r) =>
        r.excId === row.excId ? { ...r, allowedSeat: nextValue } : r
      )
    );
    const res = await updateShorexPackage(row.excId, {
      allowedSeat: nextValue,
    });
    if (res?.error) {
      setRows((prev) =>
        prev.map((r) =>
          r.excId === row.excId ? { ...r, allowedSeat: previousValue } : r
        )
      );
    } else {
      addlog(
        `${LOG_ACTIONS.UPDATED} (Slots open for app booking: ${previousValue} -> ${nextValue})`,
        LOG_TYPES.SHOREX,
        { id: row.excId, title: row.name, excNo: row.excNo, packageId: selectedSailing?.package_id },
        userDetails.id
      );
    }
  };

  const handleCutOffTimeChange = async (row, nextValue) => {
    const previousValue = row.cutOffTime;
    setRows((prev) =>
      prev.map((r) => (r.excId === row.excId ? { ...r, cutOffTime: nextValue } : r))
    );
    const res = await updateShorexPackage(row.excId, { cutOffTime: nextValue });
    if (res?.error) {
      setRows((prev) =>
        prev.map((r) => (r.excId === row.excId ? { ...r, cutOffTime: previousValue } : r))
      );
    } else {
      const formatTime = (val) => (val ? moment(val).format("DD-MM-YYYY hh:mm A") : "None");
      addlog(
        `${LOG_ACTIONS.UPDATED} (End Time: ${formatTime(previousValue)} -> ${formatTime(nextValue)})`,
        LOG_TYPES.SHOREX,
        { id: row.excId, title: row.name, excNo: row.excNo, packageId: selectedSailing?.package_id },
        userDetails.id
      );
    }
  };

  const sailingStart = selectedSailing?.sailing_date
    ? moment(selectedSailing.sailing_date)
    : null;
  const sailingEnd = selectedSailing?.sailing_date
    ? moment(selectedSailing.sailing_date).add(selectedSailing.duration, "days")
    : null;

  const handleSyncNow = async () => {
    if (!sailingStart || !sailingEnd) return;
    setSyncing(true);
    const startDate = sailingStart.format("YYYY-MM-DD");
    const endDate = sailingEnd.format("YYYY-MM-DD");
    const result = await syncShorexExcursions(
      startDate,
      endDate,
      selectedSailing.package_id
    );
    if (result?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: result.error, type: "error" },
      });
    } else {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: result?.message || "Sync done", type: "success" },
      });
      const vesselName = vesselOptions.find((v) => v.id === vesselId)?.name;
      addlog(
        LOG_ACTIONS.SYNCED,
        LOG_TYPES.SHOREX,
        {
          title: `${vesselName} (${startDate} to ${endDate}) - ${result?.data?.length ?? 0} excursion(s)`,
          packageId: selectedSailing?.package_id,
        },
        userDetails.id
      );
      setPage(1);
      loadShorexPackages(1);
    }
    setSyncing(false);
  };

  const formatSailingLabel = (sailing) =>
    `${moment(sailing.sailing_date).format("D MMM YYYY")} - ${moment(
      sailing.sailing_date
    )
      .add(sailing.duration, "days")
      .format("D MMM YYYY")} (${sailing.duration + 1}d/${sailing.duration}n)`;

  const columns = [
    {
      title: "Sl",
      key: "sl",
      align: "center",
      width: 60,
      render: (value, row, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Set Active",
      key: "linkEnabled",
      align: "center",
      width: 100,
      sortable: true,
      render: (value, row) => (
        <Switch
          checked={!!row.linkEnabled}
          onChange={() => handleToggleLink(row)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "rgb(215 24 42)",
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "rgb(215 24 42)",
            },
          }}
        />
      ),
    },
    { title: "Excursion Date", key: "startDate", align: "center", width: 120 },
    { title: "Code", key: "excNo", sortable: true },
    { title: "Name", key: "name", width: 350 },
    { title: "Adult Price", key: "adultPrice", align: "right" },
    { title: "Child Price", key: "childPrice", align: "right" },
    { title: "Max", key: "maxSeat", align: "center" },
    {
      title: "Mobile Booking",
      key: "totalBookings",
      align: "center",
      render: (value) => (value === null || value === undefined ? "-" : value),
    },
    {
      title: "Slots open for app booking",
      key: "allowedSeat",
      align: "center",
      width: 160,
      render: (value, row) => (
        <AllowedSeatInput row={row} onCommit={handleAllowedSeatChange} />
      ),
    },
    {
      title: "End Time",
      key: "cutOffTime",
      align: "center",
      width: 200,
      render: (value, row) => (
        <CutOffTimeInput row={row} onCommit={handleCutOffTimeChange} />
      ),
    }
  ];

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Shore Excursion Management", route: PAGE_PATH },
  ];

  return (
    <Layout type={"read_per"} module_data={"Management"}>
      <style>
        {`.shorex-date-picker, .shorex-date-picker input { cursor: pointer; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
      </style>
      <div>
        <HeaderBread navigation={navigation} title={"Shore Excursion Management"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Shore Excursion Management", value: "management" },
              { title: "History Logs", value: "logs" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab !== "management" ? (
            <Logs type={LOG_TYPES.SHOREX} extendedColors />
          ) : (
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Shore Excursion Management</h5>
                <Button
                  variant="contained"
                  startIcon={
                    <SyncIcon
                      style={syncing ? { animation: "spin 1s linear infinite" } : {}}
                    />
                  }
                  onClick={handleSyncNow}
                  disabled={syncing || !selectedSailing}
                  style={{ background: "#37474f", fontWeight: 500, whiteSpace: "nowrap" }}
                >
                  {syncing ? "Syncing..." : "Sync Now"}
                </Button>
              </div>
              <div className="card-body">
                <div className="table_holder">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                    }}
                  >
                    <SwitchBar
                      tabs={vesselOptions.map((item) => ({
                        title: item.name,
                        value: item.id,
                      }))}
                      activeTab={vesselId}
                      setActiveTab={(val) => {
                        setVesselId(val);
                        setSailingOptions([]);
                        setSelectedSailing(null);
                        setDate("");
                        setPage(1);
                      }}
                    />

                    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={guideLangEnabled}
                            onChange={handleGuideLangToggle}
                            disabled={!vesselId}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "rgb(215 24 42)",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "rgb(215 24 42)",
                              },
                            }}
                          />
                        }
                        label="Guide Language"
                      />
                      <div style={{ maxWidth: 200 }}>
                        <SearchField
                          value={search}
                          setValue={(e) => setSearch(e.target.value)}
                          SearchValue={() => setPage(1)}
                          placeholder="Search"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "32px",
                      margin: "24px 0",
                    }}
                  >
                    <div style={{ flex: "0 0 auto" }}>
                      <strong>Active Cruise: </strong>
                      <Select
                        size="small"
                        value={selectedSailing?.id || ""}
                        displayEmpty
                        disabled={sailingLoading || !sailingOptions.length}
                        onChange={(e) => {
                          const sailing = sailingOptions.find(
                            (s) => s.id === e.target.value
                          );
                          setSelectedSailing(sailing || null);
                          setPage(1);
                        }}
                        style={{ width: "360px" }}
                      >
                        {!sailingOptions.length && (
                          <MenuItem value="" disabled>
                            {sailingLoading ? "Loading..." : "No cruises found"}
                          </MenuItem>
                        )}
                        {sailingOptions.map((sailing) => (
                          <MenuItem key={sailing.id} value={sailing.id}>
                            {formatSailingLabel(sailing)}
                            {sailing.status === "active" ? " (Active)" : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>

                    <DatePicker
                      value={date ? dayjs(date, "YYYY-MM-DD") : null}
                      disabledDate={(current) => {
                        if (!sailingStart || !sailingEnd) return true;
                        // Embark day itself has no excursions (excluded), only
                        // the days after it through and including the
                        // turnaround/disembark day are valid.
                        return (
                          !current.isAfter(dayjs(sailingStart.format("YYYY-MM-DD")), "day") ||
                          current.isAfter(dayjs(sailingEnd.format("YYYY-MM-DD")), "day")
                        );
                      }}
                      onChange={(value) => {
                        setDate(value ? value.format("YYYY-MM-DD") : "");
                        setPage(1);
                      }}
                      placeholder="Date"
                      disabled={!selectedSailing}
                      className="shorex-date-picker"
                      style={{ width: "160px", flex: "0 0 auto", cursor: "pointer" }}
                    />

                    <strong style={{ flex: "0 0 auto" }}>
                      {date
                        ? moment(date).format("DD MMMM YYYY")
                        : sailingStart && sailingEnd
                        ? `${sailingStart.format("DD MMMM YYYY")} - ${sailingEnd.format(
                            "DD MMMM YYYY"
                          )}`
                        : ""}{" "}
                      : {totalCount} Excursions
                    </strong>
                  </div>

                  <SortableTable
                    columns={columns}
                    rows={rows}
                    page={page}
                    setPage={setPage}
                    count={totalCount}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onSortChange={handleSortChange}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ShoreExcursionsManagement;
